import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, AlertCircle, CheckCircle2, XCircle, IndianRupee, Package } from 'lucide-react';
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";

const UpdateReturnStatus = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [purchaseReturn, setPurchaseReturn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    returnstatus: 'PENDING',
    refundamount: '',
    refundmethod: '',
    refunddate: '',
    notes: ''
  });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');

  const statusOptions = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'PROCESSED', label: 'Processed' },
    { value: 'REFUNDED', label: 'Refunded' }
  ];

  const refundMethods = [
    { value: 'CASH', label: 'Cash' },
    { value: 'CHEQUE', label: 'Cheque' },
    { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
    { value: 'CREDIT_NOTE', label: 'Credit Note' },
    { value: 'ADJUSTMENT', label: 'Adjustment' }
  ];

  useEffect(() => {
    fetchPurchaseReturnDetails();
  }, [id]);

  const fetchPurchaseReturnDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/purchase-returns/${id}`);
      const data = response.data;
      setPurchaseReturn(data);

      setFormData({
        returnstatus: data.return_status,
        refundamount: data.refund_amount || '',
        refundmethod: data.refund_method || '',
        refunddate: data.refund_date ? data.refund_date.split('T')[0] : '',
        notes: data.notes || ''
      });
    } catch (error) {
      console.error('Error fetching purchase return details:', error);
      showAlertMessage('Error loading return details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showAlertMessage = (message, type = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = {
        returnstatus: formData.returnstatus,
        refundamount: formData.refundamount ? parseFloat(formData.refundamount) : null,
        refundmethod: formData.refundmethod || null,
        refunddate: formData.refunddate || null,
        notes: formData.notes
      };

      await axiosInstance.put(`/purchase-returns/${id}/status`, submitData);
      showAlertMessage('Return status updated successfully!', 'success');

      setTimeout(() => {
        navigate(`/purchase-return`);
      }, 2000);

    } catch (error) {
      console.error('Error updating return status:', error);
      const errorMessage = error.response?.data?.error || 'Error updating return status';
      showAlertMessage(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Authenticated>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </Authenticated>
    );
  }

  if (!purchaseReturn) {
    return (
      <Authenticated>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Purchase return not found</h3>
          </div>
        </div>
      </Authenticated>
    );
  }

  return (
    <Authenticated>
      <div className="min-h-screen bg-gray-50 py-6 px-2 sm:px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Alert */}
          {showAlert && (
            <div className={`fixed top-4 right-4 z-50 p-4 rounded-xl shadow-lg ${alertType === 'success' ? 'bg-green-500' : 'bg-red-500'
              } text-white max-w-md`}>
              <div className="flex items-center justify-between">
                <span>{alertMessage}</span>
                <button
                  onClick={() => setShowAlert(false)}
                  className="text-white hover:text-gray-200 ml-4"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate(`/purchase-return`)}
                  className="p-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft size={20} />
                </button>
                <div className="bg-gradient-to-br from-teal-500 to-teal-600 p-3 rounded-xl shadow-md">
                  <CheckCircle2 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Update Return Status</h1>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Return #{purchaseReturn.id} - {purchaseReturn.supplier.name}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Status Update Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-teal-600" />
                  </div>
                  Status Information
                </h2>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Return Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.returnstatus}
                      onChange={(e) => handleInputChange('returnstatus', e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-medium hover:border-teal-300 transition-colors"
                    >
                      {statusOptions.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {(formData.returnstatus === 'REFUNDED' || formData.returnstatus === 'PROCESSED') && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Refund Amount
                          </label>
                          <div className="relative">
                            <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max={purchaseReturn.total_return_amt}
                              value={formData.refundamount}
                              onChange={(e) => handleInputChange('refundamount', e.target.value)}
                              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                              placeholder="0.00"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Max: ₹{parseFloat(purchaseReturn.total_return_amt).toFixed(2)}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Refund Method
                          </label>
                          <select
                            value={formData.refundmethod}
                            onChange={(e) => handleInputChange('refundmethod', e.target.value)}
                            className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                          >
                            <option value="">Select Method</option>
                            {refundMethods.map((method) => (
                              <option key={method.value} value={method.value}>
                                {method.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Refund Date
                        </label>
                        <input
                          type="date"
                          value={formData.refunddate}
                          onChange={(e) => handleInputChange('refunddate', e.target.value)}
                          className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Add any additional notes about the status update..."
                    />
                  </div>

                  {formData.returnstatus === 'REJECTED' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start">
                        <AlertCircle className="text-red-500 mt-0.5 mr-3" size={16} />
                        <div className="text-sm text-red-700">
                          <p className="font-medium">Important: Rejecting this return</p>
                          <p>Stock quantities will be automatically restored to their original levels.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-start items-center space-x-3 mt-8">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-6 py-2.5 rounded-lg flex items-center ${isSubmitting
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-teal-600 text-white hover:bg-teal-700 shadow-md hover:shadow-lg'
                      } transition-all duration-200`}
                  >
                    <Save size={18} className="mr-2" />
                    {isSubmitting ? 'Updating...' : 'Update Status'}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/purchase-return`)}
                    className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>

            {/* Return Summary */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 sm:p-8 rounded-2xl shadow-lg border border-teal-100 hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <div className="p-2 bg-teal-200 rounded-lg">
                  <Package className="h-5 w-5 text-teal-700" />
                </div>
                Return Summary
              </h3>

              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-teal-200">
                  <span className="text-sm font-medium text-gray-600">Return ID</span>
                  <span className="font-semibold text-teal-700">#{purchaseReturn.id}</span>
                </div>

                <div className="flex justify-between items-center pb-4 border-b border-teal-200">
                  <span className="text-sm font-medium text-gray-600">Supplier</span>
                  <span className="font-semibold text-right text-gray-900">{purchaseReturn.supplier.name}</span>
                </div>

                <div className="flex justify-between items-center pb-4 border-b border-teal-200">
                  <span className="text-sm font-medium text-gray-600">Items Count</span>
                  <span className="font-semibold text-gray-900">{purchaseReturn.returnitems?.length || 0}</span>
                </div>

                <div className="flex justify-between items-center pb-4 border-b border-teal-200">
                  <span className="text-sm font-medium text-gray-600">Return Date</span>
                  <span className="font-semibold text-gray-900">
                    {new Date(purchaseReturn.return_date).toLocaleDateString('en-GB')}
                  </span>
                </div>

                <div className="flex justify-between items-center bg-gradient-to-r from-teal-500 to-teal-600 text-white p-4 rounded-xl mt-6">
                  <span className="font-semibold">Total Amount</span>
                  <div className="flex items-center text-lg font-bold">
                    <IndianRupee size={18} className="mr-1" />
                    ₹{parseFloat(purchaseReturn.total_return_amt).toFixed(2)}
                  </div>
                </div>

                {parseFloat(purchaseReturn.refund_amount) > 0 && (
                  <div className="flex justify-between items-center bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
                    <span className="text-sm font-medium text-gray-700">Current Refund</span>
                    <div className="flex items-center text-green-700 font-semibold">
                      <IndianRupee size={16} className="mr-1" />
                      ₹{parseFloat(purchaseReturn.refund_amount).toFixed(2)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Authenticated>
  );
};

export default UpdateReturnStatus;